import { formatDate } from "../../../utils/date-format.utils";
import {
  IContentCreator,
  TWorkOrderResults,
  WorkOrdersState,
} from "../../../redux/reducers/workOrdersReducer";
import { LOADING_STATES } from "../../../features/loading";

import { WorkOrderProps } from "../components/work-order/work-order.props";
import { createWorkOrderProductsState } from "../features/work-order-products/types/work-order-products.functions";
import { UnitLink } from "../../../features/new-work-order-briefs/work-order-templates/paid-media/types/unit-link/unit-link.interface";

export const createWorkOrdersState = (): WorkOrdersState => ({
  count: 0,
  currentTab: 0,
  data: createEmptyWorkOrderData(),
  errors: null,
  formErrors: null,
  filters: { assignees: [], creativePrimes: [] },
  loading: { ...LOADING_STATES.default },
  next: null,
  parentStatus: "draft",
  previous: null,
  productsState: createWorkOrderProductsState(),
  results: [],
  showAddProductModal: false,
});

export const createEmptyWorkOrderData = (): TWorkOrderResults => ({
  adMedium: "",
  adType: "",
  adFormat: "",
  adSpecifications: "",
  additionalAssetsLocation: "",
  additionalCreativeDetails: "",
  areAdditionalAssetsProvided: false,
  areVendorAssetsProvided: false,
  assetDescription: "",
  assetDimension: "",
  assetLanguages: [],
  assetRequest: null,
  assetSize: null,
  assetType: "",
  brandsIncluded: "",
  breadcrumbInfo: { parentName: "", grandparentName: "" },
  campaignTag: "",
  colour: null,
  communicationsApproach: "",
  contentCreators: [],
  contentDirection: "",
  contentName: "",
  copyDirection: "",
  createdAt: "",
  creativeDirection: "",
  description: "",
  dueAt: "",
  endAt: "",
  englishAssetUnitLinks: [],
  executionBrief: 0,
  executionBriefName: "",
  fileFormat: 0,
  fileFormatDescription: "",
  frenchAssetUnitLinks: [],
  funnel: "",
  grandparentStatus: "draft",
  hasVendorCommitment: false,
  hierarchyDetails: {
    campaignBriefId: 0,
    campaignBriefOwnerId: 0,
    executionBriefId: 0,
    executionBriefMerchOwners: [],
    executionBriefOwnerId: 0,
  },
  id: 0,
  isBuyGet: false,
  isCreativeReviewRequired: false,
  isRegional: false,
  isSkuListRequired: false,
  isUnderNda: false,
  isVendorApprovalRequired: false,
  legalDetails: "",
  mandatoryCreativeDetails: "",
  name: "",
  objectiveOrPurpose: "",
  offerDetails: "",
  owner: 0,
  ownerDetail: {
    id: 0,
    firstName: "",
    lastName: "",
    email: "",
    profileImageUrl: "",
  },
  ownerName: "",
  phdBudget: "",
  parentStatus: "draft",
  polymorphicCtype: null,
  popStoreTypes: [],
  portalOrPrint: "",
  positions: [],
  primaryMessage: "",
  projectOverview: "",
  reportingMasterCampaignName: "",
  regions: [],
  resourcetype: "",
  secondaryMessage: "",
  size: "",
  skuListUploadLocation: "",
  startAt: "",
  status: "draft",
  theme: "",
  traffickingDueAt: "",
  underNdaUserDetails: [],
  updatedAt: "",
  uploadLocation: "",
  vendorAssetsLocation: "",
  warehouseEta: "",
});

const removeEmptyUnitLinks = (links: UnitLink[]): UnitLink[] => {
  return links.filter((link) => isLinkValid(link));
};

const isLinkValid = (link: UnitLink): boolean => {
  return link.unitLink !== "";
};

const mapContentCreators = (creators: IContentCreator[]) => {
  return creators.map((creator) => ({
    id: creator.id ?? null,
    is_creative_prime: creator.isCreativePrime,
    user: creator.userDetails.id,
    workOrder: creator.workOrder,
  }));
};

export const mapFormToParams = (form: WorkOrderProps) => {
  return {
    adMedium: form.adMedium,
    adType: form.adType,
    adFormat: form.adFormat,
    adSpecifications: form.adSpecifications,
    additionalAssetsLocation: form.additionalAssetsLocation,
    additionalCreativeDetails: form.additionalCreativeDetails,
    areAdditionalAssetsProvided: form.areAdditionalAssetsProvided,
    areVendorAssetsProvided: form.areVendorAssetsProvided,
    assetDescription: form.assetDescription,
    assetDimension: form.assetDimension,
    assetLanguages: form.assetLanguages,
    assetRequest: form.assetRequest,
    assetSize: form.assetSize,
    assetType: form.assetType,
    brandsIncluded: form.brandsIncluded,
    campaignTag: form.campaignTag,
    colour: form.colour || null,
    communicationsApproach: form.communicationsApproach,
    contentCreators: mapContentCreators(form.contentCreators),
    contentDirection: form.contentDirection,
    contentName: form.contentName,
    copyDirection: form.copyDirection,
    creativeDirection: form.creativeDirection,
    cta: form.cta,
    description: form.description,
    dueAt: formatDate(form.dueAt),
    deploymentAt: form.deploymentAt,
    endAt: formatDate(form.endAt),
    assetUnitLinks: removeEmptyUnitLinks([
      ...(form.englishAssetUnitLinks ?? []),
      ...(form.frenchAssetUnitLinks ?? []),
    ]),
    englishNumberOfStores: form.englishNumberOfStores,
    englishNumberOfScreens: form.englishNumberOfScreens,
    englishQtyPerStore: form.englishQtyPerStore,
    englishRequired: form.englishRequired,
    fileFormat: form.fileFormat,
    fileFormatDescription: form.fileFormatDescription,
    frenchNumberOfStores: form.frenchNumberOfStores,
    frenchNumberOfScreens: form.frenchNumberOfScreens,
    frenchQtyPerStore: form.frenchQtyPerStore,
    frenchRequired: form.frenchRequired,
    fromPricing: form.fromPricing,
    funnel: form.funnel,
    hasVendorCommitment: form.hasVendorCommitment,
    isBuyGet: form.isBuyGet,
    isCreativeReviewRequired: form.isCreativeReviewRequired,
    isRegional: form.isRegional,
    isSegmented: form.isSegmented,
    isVendorApprovalRequired: form.isVendorApprovalRequired,
    isSkuListRequired: form.isSkuListRequired,
    legal: form.legal,
    legalDetails: form.legalDetails,
    mandatoryCreativeDetails: form.mandatoryCreativeDetails,
    name: form.workOrderName,
    notesForCreative: form.notesForCreative,
    notesForEcomm: form.notesForEcomm,
    objectiveOrPurpose: form.objectiveOrPurpose,
    offerDetails: form.offerDetails,
    phdBudget: form.phdBudget,
    polymorphicCtype: form.polymorphicCtype,
    portalOrPrint: form.portalOrPrint,
    primaryMessage: form.primaryMessage,
    projectOverview: form.projectOverview,
    preHeaderDirection: form.preHeaderDirection,
    popStoreTypes: form.popStoreTypes,
    positionDescription: form.positionDescription,
    positions: form.positions,
    regions: form.regions,
    reportingMasterCampaignName: form.reportingMasterCampaignName,
    resourcetype: form.workOrderTemplate,
    subjectLineDirection: form.subjectLineDirection,
    secondaryMessage: form.secondaryMessage,
    segments: form.segments,
    sitePlacement: form.sitePlacement,
    size: form.size,
    skuListUploadLocation: form.skuListUploadLocation,
    startAt: formatDate(form.startAt),
    theme: form.theme,
    traffickingDueAt: formatDate(form.traffickingDueAt),
    uploadLocation: form.uploadLocation,
    utmCampaign: form.utmCampaign,
    utmMedia: form.utmMedia,
    utmSource: form.utmSource,
    utmContent: form.utmContent,
    utmTerm: form.utmTerm,
    vendorAssetsLocation: form.vendorAssetsLocation,
    warehouseEta: formatDate(form.warehouseEta),
  };
};
